import { StoredColumnConfig } from '@core/store/user-preferences/user-preferences.model';
import { isNil } from '@core/utils/shared-utils';

import {
  isReferenceCellType,
  referenceUidsOf
} from '@shared/components/table/table-cell/utils';
import { TableColumn } from '@shared/components/table/table.model';
import { getCellValue } from '@shared/utils/table-cell.utils';

/** A reference column resolved to name mode, paired with its target entity type. */
export interface NameModeReferenceColumn<T> {
  column: TableColumn<T>;
  entityType: string;
}

/**
 * The single source of truth for "which reference columns are currently in name
 * mode". A column qualifies when it targets a reference entity, renders as a
 * reference cell (`isReferenceCellType`), and the PERSISTED per-column config sets its
 * `displayMode` to `'name'`.
 *
 * `displayMode` lives only on the stored per-table config (the NgRx store /
 * backend blob), never on the raw columns produced by `EntityTableService`, so
 * this joins the raw columns (for `key` / `type` / `referenceEntityType`) with
 * the stored config (for `displayMode`) by column key. The stored key is
 * `String(column.key)`, which equals the raw column key (both `raw.<field>`).
 *
 * Kept as a pure function — rather than inline in the component — so the rule
 * has one documented, independently testable home. Sourcing `displayMode` from
 * the raw columns instead of the store is exactly the regression that shipped
 * before (they never carry it); centralising the rule guards against a repeat.
 */
export function selectNameModeReferenceColumns<T>(
  columns: TableColumn<T>[],
  storedConfig: StoredColumnConfig[] | undefined
): NameModeReferenceColumn<T>[] {
  const stored = storedConfig ?? [];
  if (stored.length === 0) return [];

  const nameModeKeys = new Set(
    stored.filter((cfg) => cfg.displayMode === 'name').map((cfg) => cfg.key)
  );
  if (nameModeKeys.size === 0) return [];

  return columns
    .filter(
      (col) =>
        !isNil(col.referenceEntityType) &&
        isReferenceCellType(col.type) &&
        nameModeKeys.has(String(col.key))
    )
    .map((col) => ({ column: col, entityType: col.referenceEntityType as string }));
}

/**
 * Collects the referenced uids visible on `rows` for the given name-mode
 * columns, grouped by target entity type and de-duplicated.
 *
 * Values are read through `getCellValue` so that computed columns run their
 * `renderHook` — columns such as `raw.pickup.site` have no literal row path
 * (`raw.pickup` is an array) and only produce their uids via the hook. This
 * mirrors exactly what the cell renders, so the resolved set matches the
 * displayed set. Non-string / empty uids are ignored.
 */
export function collectReferenceUidsByType<T>(
  cols: NameModeReferenceColumn<T>[],
  rows: T[]
): Map<string, string[]> {
  const byType = new Map<string, Set<string>>();

  for (const { column, entityType } of cols) {
    for (const row of rows) {
      const value = getCellValue(row, column);
      const uids = referenceUidsOf(value);
      for (const uid of uids) {
        if (typeof uid !== 'string' || uid === '') continue;
        if (!byType.has(entityType)) byType.set(entityType, new Set());
        byType.get(entityType)!.add(uid);
      }
    }
  }

  return new Map([...byType].map(([type, uids]) => [type, [...uids]]));
}
